import React from 'react';
import {
  Stack,
  Typography,
  TextField
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers';
import { format, startOfDay } from 'date-fns';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { createExternalChargeRecovery } from 'services/requests/contract';
import { toast } from 'react-toastify';

const ExternalChargeRecoveryForm = ({ externalChargeId, onSuccess, onClose }) => {
  const validationSchema = yup.object({
    amountReceived: yup.number()
      .required('Valor recuperado é obrigatório')
      .min(0.01, 'Valor deve ser maior que zero'),
    recoveryDate: yup.date()
      .required('Data da recuperação é obrigatória'),
    recoveryExternalId: yup.string(),
    observation: yup.string()
  });

  const getInitialValues = () => {
    return {
      amountReceived: '',
      recoveryDate: new Date(),
      recoveryExternalId: '',
      observation: ''
    };
  };

  const handleFormSubmit = async (values, actions) => {
    try {
      if (!values.recoveryDate || isNaN(new Date(values.recoveryDate).getTime())) {
        actions.setErrors({ recoveryDate: 'Data inválida' });
        return;
      }

      // Normaliza para o início do dia
      const localDate = startOfDay(new Date(values.recoveryDate));

      // Soma 1 dia para compensar o deslocamento
      const compensatedDate = new Date(localDate);
      compensatedDate.setDate(compensatedDate.getDate() + 1);

      const body = {
        amountReceived: Number(values.amountReceived),
        recoveryDate: format(compensatedDate, 'yyyy-MM-dd'), // envia +1 dia
        recoveryExternalId: values.recoveryExternalId || undefined,
        observation: values.observation || undefined
      };

      await createExternalChargeRecovery({ externalChargeId, body });

      toast('Recuperação registrada com sucesso!', 'success');
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error(error);
      toast(error.message || 'Erro ao registrar recuperação', 'error');
      actions?.setErrors({ submit: error.message });
    } finally {
      actions?.setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
    >
      {({ values, setFieldValue, isSubmitting, errors, touched, actions }) => (
        <Form>
          <Stack direction="column" spacing={3} sx={{ 
            minWidth: { xs: '100%', sm: 400 },
            maxWidth: '100%'
          }}>
            <Typography variant="body2" color="text.secondary">
              Registre o valor recuperado da cobrança externa.
            </Typography>
            
            <TextField
              fullWidth
              label="Valor Recuperado"
              type="number"
              value={values.amountReceived}
              onChange={(e) => setFieldValue('amountReceived', e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
              helperText={touched.amountReceived && errors.amountReceived ? errors.amountReceived : "Valor total recuperado da cobrança externa"}
              error={touched.amountReceived && Boolean(errors.amountReceived)}
              required
            />

            <DatePicker
              label="Data da Recuperação"
              value={values.recoveryDate}
              onChange={(selectedDate) => {
                if (selectedDate) {
                  setFieldValue('recoveryDate', selectedDate);
                }
              }}
              maxDate={new Date()}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  helperText={touched.recoveryDate && errors.recoveryDate ? errors.recoveryDate : "Data em que a recuperação foi realizada"}
                  error={touched.recoveryDate && Boolean(errors.recoveryDate)}
                  required
                />
              )}
            />

            <TextField
              fullWidth
              label="ID Externo da Recuperação (Opcional)"
              value={values.recoveryExternalId}
              onChange={(e) => setFieldValue('recoveryExternalId', e.target.value)}
              helperText="Identificador externo da recuperação (opcional)"
            />

            <TextField
              fullWidth
              label="Observação (Opcional)"
              multiline
              rows={3}
              value={values.observation}
              onChange={(e) => setFieldValue('observation', e.target.value)}
              helperText="Observações sobre a recuperação"
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <LoadingButton
                variant="outlined"
                color="secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Fechar
              </LoadingButton>

              <LoadingButton
                type="submit"
                variant="contained"
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Registrar Recuperação
              </LoadingButton>
            </Stack>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default ExternalChargeRecoveryForm;